import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import {
  Button,
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  DropdownMultiSelectAdvanced,
  Icon,
  NavTabs,
  NavTabsItem,
  SmartTable,
  Tooltip,
  UtilityInlineGrid,
} from 'rhinostyle';
import { convertUtcStringToLocalMoment } from '../helpers/DateHelpers';
import { formatName, formatMemberNames, userHasAnyOfPermissions } from '../helpers/UserHelpers';
import { calculatePastDateRange, calculateFutureDateRange } from '../helpers/AppointmentHelpers';
import {
  NAVIGATION_TABS,
  PAST_APPOINTMENT_OPTIONS,
  UPCOMING_APPOINTMENT_OPTIONS,
} from '../constants/AppointmentManagerConstants';
import { AppConstants, UserPermissionsConstants } from '../constants';
import CancelScheduledMessageModal from './CancelScheduledMessageModal';
import EmptyMessage from './EmptyMessage';
import ScheduledMessageModal from './ScheduledMessageModal';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const EVENT_STATUS_DELIVERED = [38, 39];
const EVENT_STATUS_FAILED = [40, 41];

function getDisplayStatus(message) {
  if (message.status === 'sending' && message.event) {
    if (EVENT_STATUS_DELIVERED.includes(message.event.eventStatusTypeId)) return 'sent';
    if (EVENT_STATUS_FAILED.includes(message.event.eventStatusTypeId)) return 'failed';
  }
  return message.status;
}

const ALERT_LABELS = {
  phone_deleted: 'Phone deleted',
  opted_out: 'Contact opted out',
};

const ScheduledMessageManager = (props) => {
  const {
    scheduledMessages,
    activeTab,
    status,
    alertOnly,
    dateFrom,
    dateTo,
    activeKey,
    statusOptions,
    isDeleteModalOpen,
    isEditModalOpen,
    editingMessage,
    members,
    membersIds,
    selectedMemberIds,
    selectedMembers,
    contacts,
    contactsIds,
    selectedContactIds,
    selectedContacts,
    onTabChange,
    onStatusChange,
    onAlertToggle,
    onDateChange,
    onDeleteClick,
    onDeleteConfirm,
    onDeleteCancel,
    onEditClick,
    onEditConfirm,
    onEditClose,
    onRescheduleClick,
    onUpdateSelectedIds,
    onClearSelectedMembers,
    onClearSelectedContacts,
    onFetchFilterData,
    navigateToConversation,
  } = props;

  const isUpcoming = activeTab === NAVIGATION_TABS.upcomingAppointment;
  const dateDropdownOptions = isUpcoming ? UPCOMING_APPOINTMENT_OPTIONS : PAST_APPOINTMENT_OPTIONS;
  const defaultDateRange = isUpcoming ? calculateFutureDateRange : calculatePastDateRange;

  const smartTableColumns = [
    {
      Header: 'Scheduled Date/Time',
      accessor: 'scheduledAt',
      minWidth: 170,
      Cell: (row) => (
        <div>{convertUtcStringToLocalMoment(row.value).format('MM/DD/YY hh:mm a')}</div>
      ),
    },
    {
      Header: 'Contact',
      accessor: 'toUser',
      minWidth: 160,
      Cell: (row) => (
        row.value ? (
          <Button type="link" size="small" onClick={() => navigateToConversation(row.value.id)}>
            {formatName(row.value)}
          </Button>
        ) : <div>—</div>
      ),
    },
    {
      Header: 'Member',
      accessor: 'sender',
      minWidth: 160,
      Cell: (row) => <div>{row.value ? formatName(row.value) : '—'}</div>,
    },
    {
      Header: 'Channel',
      accessor: 'channel',
      minWidth: 130,
      Cell: (row) => <div>{row.value ? row.value.name : '—'}</div>,
    },
    {
      Header: 'Message',
      accessor: 'messageText',
      minWidth: 70,
      Cell: (row) => (
        <div className="analytics__message-icon">
          <Tooltip content={<div className="analytics__tooltip-box"><p className="analytics__tooltip-message">{row.value}</p></div>} delay={false}>
            <Icon icon="note" />
          </Tooltip>
        </div>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      minWidth: 100,
      Cell: (row) => {
        const displayStatus = getDisplayStatus(row.original);
        return <div className={displayStatus === 'failed' ? 'u-text-danger' : ''}>{STATUS_LABELS[displayStatus] || displayStatus}</div>;
      },
    },
    {
      Header: 'Alert',
      accessor: 'hasAlert',
      minWidth: 70,
      className: 'u-text-center',
      Cell: (row) => (
        row.value ? (
          <Tooltip placement="top" content={ALERT_LABELS[row.original.alertType] || 'Alert'}>
            <span><Icon icon="alert-triangle" className="u-text-danger" /></span>
          </Tooltip>
        ) : null
      ),
    },
    {
      Header: 'Actions',
      accessor: 'id',
      minWidth: 140,
      sortable: false,
      Cell: (row) => {
        if (!userHasAnyOfPermissions([UserPermissionsConstants.SCHEDULED_MESSAGE_EDIT])) return null;
        const displayStatus = getDisplayStatus(row.original);
        if (displayStatus === 'scheduled') {
          return (
            <UtilityInlineGrid>
              <Button type="link" size="small" onClick={() => onEditClick(row.original)}>Edit</Button>
              <Button type="link-danger" size="small" onClick={() => onDeleteClick(row.value)}>Cancel</Button>
            </UtilityInlineGrid>
          );
        }
        if (displayStatus === 'failed') {
          return (
            <Button type="link" size="small" onClick={() => onRescheduleClick(row.original)}>Reschedule</Button>
          );
        }
        return null;
      },
    },
  ];

  const selectedStatusLabel = status
    ? (statusOptions.find((o) => o.id === status) || {}).label
    : 'All Statuses';

  const renderFilterTitle = (selectedIds, name, initialLabel) => (
    selectedIds.length > 0 ? `${selectedIds.length} ${name} Selected` : initialLabel
  );

  const alertToggleOptions = [
    { id: false, label: 'All' },
    { id: true, label: 'Alerts Only' },
  ];

  const renderFilters = () => (
    <div className="rhinopay-wrapper__filter">
      <div className="rhinopay-wrapper__filter-label">FILTER BY:</div>
      <UtilityInlineGrid>
        <div className="filter-page__dropdown">
          <DateRangeDropdown
            position="left"
            name="Scheduled Message Date Picker"
            startDate={moment(dateFrom)}
            endDate={moment(dateTo)}
            selectDate={onDateChange}
            selectDateRange={defaultDateRange}
            dateLabelFormat="MMM D, YYYY"
            dropdownMenuItems={dateDropdownOptions}
            activeKey={activeKey}
            isCustomDate
            key={`scheduled-${activeTab}`}
          />
        </div>
        <DropdownMultiSelectAdvanced
          handleUpdateSelectedIds={onUpdateSelectedIds}
          avatarBaseUrl={AppConstants.AVATAR_BASE_URL}
          items={formatMemberNames(members)}
          selectedItems={selectedMembers}
          selectedItemsIds={selectedMemberIds}
          handleClearAllSelectedItems={onClearSelectedMembers}
          fetchAllItems={onFetchFilterData}
          itemsIds={membersIds}
          dropdownLabel={renderFilterTitle(selectedMemberIds, 'Members', 'Member(s)')}
          filterName="members"
          type="member"
          className="multi-select__dropdown__height"
        />
        <DropdownMultiSelectAdvanced
          handleUpdateSelectedIds={onUpdateSelectedIds}
          avatarBaseUrl={AppConstants.AVATAR_BASE_URL}
          items={contacts}
          selectedItems={selectedContacts}
          selectedItemsIds={selectedContactIds}
          handleClearAllSelectedItems={onClearSelectedContacts}
          fetchAllItems={onFetchFilterData}
          itemsIds={contactsIds}
          dropdownLabel={renderFilterTitle(selectedContactIds, 'Contacts', 'Contact(s)')}
          filterName="contacts"
          type="member"
          className="multi-select__dropdown__height"
        />
        <Dropdown
          label={selectedStatusLabel}
          type={status ? 'primary' : 'input'}
          outlined={!!status}
          position="bottom"
        >
          {statusOptions.map((opt) => (
            <DropdownMenuItem
              key={String(opt.id)}
              id={String(opt.id)}
              label={opt.label}
              onClick={() => onStatusChange(opt.id)}
              active={opt.id === status}
            />
          ))}
        </Dropdown>
        <Dropdown
          label={alertOnly ? 'Alerts Only' : 'All'}
          type={alertOnly ? 'primary' : 'input'}
          outlined={!!alertOnly}
          position="bottom"
        >
          {alertToggleOptions.map((opt) => (
            <DropdownMenuItem
              key={String(opt.id)}
              id={String(opt.id)}
              label={opt.label}
              onClick={() => onAlertToggle(opt.id)}
              active={opt.id === alertOnly}
            />
          ))}
        </Dropdown>
      </UtilityInlineGrid>
    </div>
  );

  const renderTable = () => (
    <div className="u-p-t">
      {scheduledMessages.length > 0 ? (
        <SmartTable
          showPagination={false}
          data={scheduledMessages}
          columns={smartTableColumns}
          minRows={0}
          sortable={false}
          striped
          defaultPageSize={scheduledMessages.length}
        />
      ) : (
        <EmptyMessage section="Scheduled Message Manager" />
      )}
    </div>
  );

  return (
    <div className="app-page__container">
      <div className="u-m-t-large u-text-center">
        <h3>Scheduled Message Manager</h3>
      </div>
      <div className="convo__tabs nav-tabs__wrapper">
        <NavTabs activeKey={activeTab} onSelect={onTabChange}>
          <NavTabsItem className="nav-tabs__item--header" id={NAVIGATION_TABS.upcomingAppointment}>Upcoming Scheduled Messages</NavTabsItem>
          <NavTabsItem className="nav-tabs__item--header" id={NAVIGATION_TABS.pastAppointment}>Past Scheduled Messages</NavTabsItem>
        </NavTabs>
      </div>
      <div className="rhinopay-wrapper">
        {renderFilters()}
        {renderTable()}
      </div>

      <CancelScheduledMessageModal
        open={isDeleteModalOpen}
        onClose={onDeleteCancel}
        onConfirm={onDeleteConfirm}
      />

      <ScheduledMessageModal
        key={editingMessage ? editingMessage.id : 'edit-modal'}
        isOpen={isEditModalOpen}
        initialScheduledAt={editingMessage && editingMessage.scheduledAt}
        initialMessageText={editingMessage && editingMessage.messageText}
        onClose={onEditClose}
        onConfirm={onEditConfirm}
      />
    </div>
  );
};

ScheduledMessageManager.propTypes = {
  scheduledMessages: PropTypes.array.isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  alertOnly: PropTypes.bool.isRequired,
  dateFrom: PropTypes.object.isRequired,
  dateTo: PropTypes.object.isRequired,
  activeKey: PropTypes.number.isRequired,
  statusOptions: PropTypes.array.isRequired,
  isDeleteModalOpen: PropTypes.bool.isRequired,
  isEditModalOpen: PropTypes.bool.isRequired,
  editingMessage: PropTypes.object,
  members: PropTypes.object.isRequired,
  membersIds: PropTypes.array.isRequired,
  selectedMemberIds: PropTypes.array.isRequired,
  selectedMembers: PropTypes.object.isRequired,
  contacts: PropTypes.object.isRequired,
  contactsIds: PropTypes.array.isRequired,
  selectedContactIds: PropTypes.array.isRequired,
  selectedContacts: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onAlertToggle: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onDeleteConfirm: PropTypes.func.isRequired,
  onDeleteCancel: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onEditConfirm: PropTypes.func.isRequired,
  onEditClose: PropTypes.func.isRequired,
  onRescheduleClick: PropTypes.func.isRequired,
  onUpdateSelectedIds: PropTypes.func.isRequired,
  onClearSelectedMembers: PropTypes.func.isRequired,
  onClearSelectedContacts: PropTypes.func.isRequired,
  onFetchFilterData: PropTypes.func.isRequired,
  navigateToConversation: PropTypes.func.isRequired,
};

export default ScheduledMessageManager;
